import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, XCircleIcon, AlertTriangleIcon, InfoIcon, XIcon } from 'lucide-react';
interface NotificationProps {
  id: number | string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  onClose: (id: number | string) => void;
}
export function Notification({
  id,
  type,
  title,
  message,
  duration = 5000,
  onClose
}: NotificationProps) {
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => onClose(id), 300); // Wait for animation to complete
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, id, onClose]);
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onClose(id), 300); // Wait for animation to complete
  };
  const typeStyles = {
    success: 'bg-green-900/30 border-green-800',
    error: 'bg-red-900/30 border-red-800',
    warning: 'bg-yellow-900/30 border-yellow-800',
    info: 'bg-blue-900/30 border-blue-800'
  };
  const typeIcons = {
    success: <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-845" />,
    error: <XCircleIcon className="h-5 w-5 text-red-400" data-id="element-846" />,
    warning: <AlertTriangleIcon className="h-5 w-5 text-yellow-400" data-id="element-847" />,
    info: <InfoIcon className="h-5 w-5 text-blue-400" data-id="element-848" />
  };
  const typeTitleColors = {
    success: 'text-green-300',
    error: 'text-red-300',
    warning: 'text-yellow-300',
    info: 'text-blue-300'
  };
  return <div className={`max-w-sm w-full rounded-lg border shadow-lg pointer-events-auto ${typeStyles[type]} transform transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`} data-id="element-849">
      <div className="p-4" data-id="element-850">
        <div className="flex items-start" data-id="element-851">
          <div className="flex-shrink-0" data-id="element-852">{typeIcons[type]}</div>
          <div className="ml-3 w-0 flex-1" data-id="element-853">
            <p className={`text-sm font-medium ${typeTitleColors[type]}`} data-id="element-854">
              {title}
            </p>
            <p className="mt-1 text-sm text-gray-300" data-id="element-855">{message}</p>
          </div>
          <div className="ml-4 flex-shrink-0 flex" data-id="element-856">
            <button onClick={handleClose} className="bg-transparent rounded-md text-gray-400 hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500" data-id="element-857">
              <span className="sr-only" data-id="element-858">Close</span>
              <XIcon className="h-5 w-5" data-id="element-859" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}
interface NotificationContainerProps {
  notifications: Array<{
    id: number | string;
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
    duration?: number;
  }>;
  onClose: (id: number | string) => void;
}
export function NotificationContainer({
  notifications,
  onClose
}: NotificationContainerProps) {
  return <div className="fixed inset-0 flex flex-col items-end px-4 py-6 pointer-events-none sm:p-6 z-50 space-y-4" data-id="element-860">
      {notifications.map(notification => <Notification key={notification.id} id={notification.id} type={notification.type} title={notification.title} message={notification.message} duration={notification.duration} onClose={onClose} data-id="element-861" />)}
    </div>;
}